/**
 * useResumedUploads — puts the files a resumed cart line owns back on screen.
 *
 * The FileAPI scope is per-session-in-memory: after a reload or a draft resume it
 * is empty, even though the files are still on the purchase session and the lines
 * still own them (`cartAttachmentsSlice`, verified against the session's own
 * listing by the resume path). Without this the drawer looked like nothing had
 * ever been uploaded, while the order would still have carried the files.
 *
 * Seeds display-only marker entries (`seed`), tagged with the line's product group
 * so the per-product readers keep them, and named with the filename the client
 * actually dropped. `remove()` then works on them like any other entry — the
 * delete handler keys off the same server filename.
 *
 * Idempotent: `seed` merges by id and anything already in the scope is skipped.
 * It stays store-derived on purpose — the FileAPI scope is rebuilt whenever the
 * session is re-ensured, and re-deriving is what puts the files back. Deleting a
 * file therefore has to UNCLAIM it (`FileUploadSection`), not just drop the scope
 * entry, or this hook would faithfully restore what the client just removed.
 */

import React, { useEffect } from 'react'

import { usePfStore } from '../store'
import { usePfV2VisualUpload } from './kinds/stagingVisuals'

export function useResumedUploads(): void {
  const attachments = usePfStore((state) => state.attachments)
  const items = usePfStore((state) => state.items)
  const { stagingEntries, seedVisuals } = usePfV2VisualUpload()

  useEffect(() => {
    for (const item of items) {
      const owned = attachments[item.id] ?? []
      if (owned.length === 0) continue

      const missing = owned.filter(
        (file) => !stagingEntries.some((entry) => (entry.gcFilename ?? entry.id) === file.gcFilename)
      )
      if (missing.length === 0) continue

      seedVisuals(
        missing.map((file) => file.gcFilename),
        // Same grouping key the upload used, or a per-product reader filters the
        // recovered file straight back out.
        item.shellProductId,
        Object.fromEntries(missing.map((file) => [file.gcFilename, file.originalFilename]))
      )
    }
  }, [attachments, items, stagingEntries, seedVisuals])
}

/**
 * Mount-point for {@link useResumedUploads}. Renders nothing; exists because the
 * hook must run INSIDE `PfV2VisualUploadProvider` and the flow root is above it.
 */
export const ResumedUploads: React.FC = () => {
  useResumedUploads()
  return null
}
